package com.swetdot.swetgram.ui.picso

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.newUuid
import com.swetdot.swetgram.data.picso.PicsoSource
import com.swetdot.swetgram.domain.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

data class PicsoUiState(
    val feedTab: PicsoFeedTab = PicsoFeedTab.ALL,
    val items: List<PicsoPostItem> = emptyList(),
    val isLoading: Boolean = false,
    val isRefreshing: Boolean = false,
    val isLoadingMore: Boolean = false,
    val nextCursor: String? = null,
    val hasMore: Boolean = false,
    val selectedPost: PicsoPostItem? = null,
    val isOpening: Boolean = false,
    val error: Boolean = false,
    val actionError: Boolean = false,
    val busyIds: Set<PicsoId> = emptySet(),
    val sharePost: PicsoPostItem? = null,
    val shareChats: List<Chat> = emptyList(),
    val isSharing: Boolean = false,
    val isLoadingChats: Boolean = false,
    val shared: Boolean = false,
)

class PicsoViewModel(
    private val picsoRepository: PicsoSource,
    private val loadChats: suspend () -> List<Chat> = { emptyList() },
    private val sharedToChat: (ChatId) -> Unit = {},
) : ViewModel() {
    private val _state = MutableStateFlow(PicsoUiState())
    val state = _state.asStateFlow()
    private var feedJob: Job? = null
    private var selectionJob: Job? = null
    private var generation = 0
    private var selectionGeneration = 0
    private val shareKeys = mutableMapOf<Pair<PicsoId, ChatId>, String>()

    init {
        refresh()
        viewModelScope.launch {
            while (isActive) {
                pruneExpired()
                delay(250)
            }
        }
    }

    fun now() = picsoRepository.now()
    suspend fun image(id: String) = picsoRepository.image(id)

    fun pruneExpired() {
        val now = now()
        _state.value = _state.value.let { current ->
            current.copy(
                items = current.items.filter { it.isActiveAt(now) },
                selectedPost = current.selectedPost?.takeIf { it.isActiveAt(now) },
                sharePost = current.sharePost?.takeIf { it.isActiveAt(now) },
            )
        }
    }

    fun switchTab(tab: PicsoFeedTab) {
        if (_state.value.feedTab == tab) return
        _state.value = _state.value.copy(feedTab = tab, items = emptyList(), nextCursor = null, hasMore = false)
        refresh()
    }

    fun refresh() {
        pruneExpired()
        _state.value = _state.value.copy(actionError = false)
        feedJob?.cancel()
        generation++
        loadFeed(refresh = true)
    }

    fun loadMore() {
        val current = _state.value
        if (feedJob?.isActive == true || !current.hasMore || current.nextCursor == null) return
        loadFeed(refresh = false)
    }

    private fun loadFeed(refresh: Boolean) {
        val current = _state.value
        val tab = current.feedTab
        val ticket = generation
        val cursor = if (refresh) null else current.nextCursor
        _state.value = current.copy(
            isLoading = refresh && current.items.isEmpty(),
            isRefreshing = refresh && current.items.isNotEmpty(),
            isLoadingMore = !refresh,
            error = false,
        )
        feedJob = viewModelScope.launch {
            try {
                val page = if (tab == PicsoFeedTab.SAVED) picsoRepository.getSaved(cursor) else picsoRepository.getFeed(tab, cursor)
                if (ticket != generation || tab != _state.value.feedTab) return@launch
                val incoming = page.items.map { it.toDomain() }
                val items = (if (refresh) incoming else _state.value.items + incoming)
                    .distinctBy { it.id }.filter { it.isActiveAt(now()) }
                _state.value = _state.value.copy(
                    items = items,
                    isLoading = false, isRefreshing = false, isLoadingMore = false,
                    hasMore = page.pageInfo.hasMore && page.pageInfo.nextCursor != cursor,
                    nextCursor = page.pageInfo.nextCursor,
                )
            } catch (c: CancellationException) {
                throw c
            } catch (_: Exception) {
                if (ticket == generation) _state.value = _state.value.copy(
                    isLoading = false, isRefreshing = false, isLoadingMore = false, error = true,
                )
            }
        }
    }

    fun selectPost(post: PicsoPostItem?) {
        selectionJob?.cancel()
        selectionGeneration++
        _state.value = _state.value.copy(selectedPost = null, isOpening = false, actionError = false)
        if (post == null || !post.isActiveAt(now())) return
        openPost(post.id.value)
    }

    fun openPost(id: String) {
        selectionJob?.cancel()
        val ticket = ++selectionGeneration
        _state.value = _state.value.copy(selectedPost = null, isOpening = true, actionError = false)
        selectionJob = viewModelScope.launch {
            try {
                var fresh = picsoRepository.getPost(id).toDomain()
                if (!fresh.isActiveAt(now())) return@launch
                if (fresh.isMine) {
                    val analytics = picsoRepository.getAnalytics(id)
                    fresh = fresh.copy(viewsCount = analytics.viewsCount, uniqueViewersCount = analytics.uniqueViewersCount)
                }
                if (ticket != selectionGeneration) return@launch
                _state.value = _state.value.copy(selectedPost = fresh)
                if (!fresh.isMine) picsoRepository.recordView(id, newUuid())
            } catch (c: CancellationException) {
                throw c
            } catch (_: Exception) {
                if (ticket == selectionGeneration) _state.value = _state.value.copy(actionError = true)
            } finally {
                if (ticket == selectionGeneration) _state.value = _state.value.copy(isOpening = false)
            }
        }
    }

    private fun item(id: PicsoId) = _state.value.selectedPost?.takeIf { it.id == id }
        ?: _state.value.items.firstOrNull { it.id == id }

    private fun mutate(id: PicsoId, operation: suspend () -> Unit) {
        if (id in _state.value.busyIds || item(id)?.isActiveAt(now()) != true) return
        _state.value = _state.value.copy(busyIds = _state.value.busyIds + id, actionError = false)
        viewModelScope.launch {
            try { operation() }
            catch (c: CancellationException) { throw c }
            catch (_: Exception) { _state.value = _state.value.copy(actionError = true) }
            finally { _state.value = _state.value.copy(busyIds = _state.value.busyIds - id); pruneExpired() }
        }
    }

    fun react(id: PicsoId, emoji: String) = mutate(id) {
        val summary = if (item(id)?.myReaction == emoji) picsoRepository.removeReaction(id.value)
            else picsoRepository.setReaction(id.value, emoji)
        updateItem(id) { it.copy(myReaction = summary.myReaction, reactionsCount = summary.reactionsCount) }
    }

    fun toggleSave(id: PicsoId) = mutate(id) {
        val status = if (item(id)?.isSaved == true) picsoRepository.unsave(id.value) else picsoRepository.save(id.value)
        updateItem(id) { it.copy(isSaved = status.isSaved) }
        if (!status.isSaved && _state.value.feedTab == PicsoFeedTab.SAVED) {
            _state.value = _state.value.copy(items = _state.value.items.filterNot { it.id == id })
        }
    }

    fun deletePost(id: PicsoId) = mutate(id) {
        picsoRepository.delete(id.value)
        _state.value = _state.value.copy(
            items = _state.value.items.filterNot { it.id == id },
            selectedPost = _state.value.selectedPost?.takeUnless { it.id == id },
        )
    }

    fun openShare(post: PicsoPostItem) {
        if (!post.isActiveAt(now())) return
        _state.value = _state.value.copy(sharePost = post, isLoadingChats = true, actionError = false, shared = false)
        viewModelScope.launch {
            try { _state.value = _state.value.copy(shareChats = loadChats()) }
            catch (c: CancellationException) { throw c }
            catch (_: Exception) { _state.value = _state.value.copy(actionError = true) }
            finally { _state.value = _state.value.copy(isLoadingChats = false) }
        }
    }

    fun dismissShare() {
        if (!_state.value.isSharing) _state.value = _state.value.copy(sharePost = null)
    }

    fun shareTo(chatId: ChatId) {
        val post = _state.value.sharePost ?: return
        if (_state.value.isSharing || !post.isActiveAt(now())) return
        val key = shareKeys.getOrPut(post.id to chatId) { newUuid() }
        _state.value = _state.value.copy(isSharing = true, actionError = false)
        viewModelScope.launch {
            try {
                picsoRepository.share(post.id.value, chatId.value, key)
                shareKeys.remove(post.id to chatId)
                _state.value = _state.value.copy(sharePost = null, selectedPost = null, shared = true)
                sharedToChat(chatId)
            } catch (c: CancellationException) { throw c }
            catch (_: Exception) { _state.value = _state.value.copy(actionError = true) }
            finally { _state.value = _state.value.copy(isSharing = false) }
        }
    }

    private fun updateItem(id: PicsoId, transform: (PicsoPostItem) -> PicsoPostItem) {
        _state.value = _state.value.let { current -> current.copy(
            items = current.items.map { if (it.id == id) transform(it) else it },
            selectedPost = current.selectedPost?.let { if (it.id == id) transform(it) else it },
        ) }
    }
}
